/**
 * Wykładniczy backoff dla połączenia WebSocket powłoki.
 *
 * DLACZEGO osobny, czysty moduł: poprzednia wersja `WebSocketProvider` po trzech
 * nieudanych próbach ustawiała `isDisabledRef = true` i już nigdy nie próbowała
 * ponownie — jedynym sposobem na odzyskanie activity feedu było przeładowanie
 * karty. Wyliczenie opóźnienia trzyma się tutaj, żeby dało się je przetestować
 * bez montowania Reacta i bez prawdziwego socketu.
 */

export interface BackoffOptions {
  /** Opóźnienie pierwszej ponowionej próby. */
  baseDelayMs: number;
  /** Górny limit — po jego osiągnięciu ponawiamy w stałym rytmie w nieskończoność. */
  maxDelayMs: number;
  /** Rozrzut ±ratio nakładany na wyliczone opóźnienie (0 = brak). */
  jitterRatio: number;
}

export const WS_BACKOFF_DEFAULTS: BackoffOptions = {
  baseDelayMs: 1_000,
  maxDelayMs: 30_000,
  jitterRatio: 0.25,
};

/**
 * Zwraca opóźnienie przed próbą numer `attempt` (1 = pierwsza ponowiona próba).
 *
 * Jitter jest obustronny, bo bez niego wszystkie karty przeglądarki i wszystkie
 * urządzenia w sieci wracałyby do bramy w tej samej milisekundzie po jej
 * restarcie i natychmiast ją znów przewracały.
 *
 * @param random wstrzykiwane źródło losowości — testy podają wartość stałą.
 */
export function computeBackoffDelay(
  attempt: number,
  options: Partial<BackoffOptions> = {},
  random: () => number = Math.random
): number {
  const { baseDelayMs, maxDelayMs, jitterRatio } = { ...WS_BACKOFF_DEFAULTS, ...options };
  const safeAttempt = Math.max(1, Math.floor(attempt));

  // 2 ** duża_liczba daje Infinity — Math.min i tak przytnie do maxDelayMs.
  const exponential = Math.min(maxDelayMs, baseDelayMs * 2 ** (safeAttempt - 1));
  const jitter = exponential * jitterRatio * (random() * 2 - 1);

  return Math.max(baseDelayMs, Math.round(exponential + jitter));
}

/** Liczba pełnych sekund pozostałych do `targetMs` (nigdy ujemna). */
export function secondsUntil(targetMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((targetMs - nowMs) / 1000));
}
